// ACA section IDs
const SECT_COMPANY_DETAILS = 'company-details';
const SECT_OWNERSHIP = 'ownership';
const SECT_FINANCIAL_DETAILS = 'financial-details';
const SECT_FUNDING_CREDIT_INFORMATION = 'funding-credit-information';
const SECT_BUSINESS_PLANS = 'business-plans';
const SECT_PERSONNEL = 'personnel';
const SECT_POLICIES_OPERATIONS = 'policies-and-operations';
const SECT_INFORMATION_INSOLVENCY = 'information-insolvency';

export const formatACA = async (licenceDetails: any, _caseTypeID: string, _application?: string) => {
    // ACA Licence View - Accreditation of Certification Authority
    // licenceDetails = content.LICApplication.licence
    // Replicates the same field mapping as ACA application-details form
    
    const licenceNode = licenceDetails || {};
    
    const applicationDetailsValues = {
        // Licence name (fallback to static if backend omits)
        'application-details-licence-name': 'Accreditation of Certification Authority',
        
        // Company details from licence.companyDetails
        ...(licenceNode.companyDetails && {
            [`${SECT_COMPANY_DETAILS}-registered-as`]: licenceNode.companyDetails.companyRegisteredAs,
            [`${SECT_COMPANY_DETAILS}-registered-as-others`]: licenceNode.companyDetails.ifOthersPleaseSpecify,
            [`${SECT_COMPANY_DETAILS}-principal-business-activities`]: licenceNode.companyDetails.principalBusinessActivities,
            [`${SECT_COMPANY_DETAILS}-company-website-url`]: licenceNode.companyDetails.companyURL
        }),
        
        // Company documents
        ...(licenceNode.companyDocuments && {
            [`${SECT_COMPANY_DETAILS}-0-company-documents-upload`]: licenceNode.companyDocuments
        }),
        
        // Ownership details - ensure at least one row
        [`${SECT_OWNERSHIP}-shareholder-list`]: licenceNode.ownership?.shareholderList?.length > 0 
            ? licenceNode.ownership.shareholderList.map((s: any) => ({
                name: s.shareHolderName,
                country: s.countryOfIncorporation,
                'share-percentage': s.sharePercent,
                'nature-of-business': s.businessNature
            }))
            : [{ name: '', country: '', 'share-percentage': '', 'nature-of-business': '' }], // Default single row
        
        // Shareholder documents
        ...(licenceNode.shareholderDocuments && {
            [`${SECT_OWNERSHIP}-0-shareholder-documents-upload`]: licenceNode.shareholderDocuments
        }),
        
        // Financial statements - ensure at least one row
        [`${SECT_FINANCIAL_DETAILS}-statement-list`]: licenceNode.financialStatementsForPastYearsList?.length > 0
            ? licenceNode.financialStatementsForPastYearsList.map((f: any) => ({
                year: f.financialYearEnd,
                revenue: f.financialrevenue,
                'profits-before-tax': f.profitsBeforeTax,
                'profits-after-tax': f.profitsAfterTax,
                'total-assets': f.totalAssets,
                'total-liabilities': f.totalLiabilities
            }))
            : [{ 
                year: '', 
                revenue: '', 
                'profits-before-tax': '', 
                'profits-after-tax': '', 
                'total-assets': '', 
                'total-liabilities': '' 
            }], // Default single row
        
        // Auditors - populated via modal, no default rows
        [`${SECT_FINANCIAL_DETAILS}-auditors-list`]: licenceNode.auditInfo?.auditorList?.length > 0
            ? licenceNode.auditInfo.auditorList.map((a: any) => ({
                name: a.auditorName,
                postal: a.address?.postalCode,
                building: a.address?.buildingName,
                'block-house': a.address?.blockNo,
                street: a.address?.streetName,
                floor: a.address?.floor,
                unit: a.address?.unit
            }))
            : [], // No default rows - added via modal
        
        // Auditor documents
        ...(licenceNode.auditorDocuments && {
            [`${SECT_FINANCIAL_DETAILS}-auditor-documents-upload`]: licenceNode.auditorDocuments
        }),
        
        // Funding credit - ensure at least one row
        [`${SECT_FUNDING_CREDIT_INFORMATION}-funding-credit-information-list`]: licenceNode.fundingCreditList?.length > 0
            ? licenceNode.fundingCreditList.map((f: any) => ({
                'source-of-funding': f.sourceOfFunding,
                amount: f.fundAmount,
                'details-of-any-collateral': f.collateralDetails,
                'name-of-contact-person-for-verification': f.contactPersonForVerification,
                'contact-number': f.contactNo,
                'email-address': f.fundingCreditEmail
            }))
            : [{ 
                'source-of-funding': '', 
                amount: '', 
                'details-of-any-collateral': '', 
                'name-of-contact-person-for-verification': '', 
                'contact-number': '', 
                'email-address': '' 
            }], // Default single row
        
        // Business Plans documents
        ...(licenceNode.businessPlansDocuments && {
            [`${SECT_BUSINESS_PLANS}-business-plans-documents-upload`]: licenceNode.businessPlansDocuments
        }),
        
        // Personnel documents
        ...(licenceNode.personnelDocuments && {
            [`${SECT_PERSONNEL}-personnel-documents-upload`]: licenceNode.personnelDocuments
        }),
        
        // Policies and Operations documents
        ...(licenceNode.policiesOperationsDocuments && {
            [`${SECT_POLICIES_OPERATIONS}-policies-and-operations-documents-upload`]: licenceNode.policiesOperationsDocuments
        }),
        
        // Insolvency info
        ...(licenceNode.InsolvencyLegalProceedingsInfo && {
            [`${SECT_INFORMATION_INSOLVENCY}-substantial-shareholder`]: licenceNode.InsolvencyLegalProceedingsInfo.isBeingWoundUpOrLiquidated,
            [`${SECT_INFORMATION_INSOLVENCY}-substantial-shareholder-details`]: licenceNode.InsolvencyLegalProceedingsInfo.beingWoundUpOrLiquidatedDetails,
            [`${SECT_INFORMATION_INSOLVENCY}-involved-legal`]: licenceNode.InsolvencyLegalProceedingsInfo.isInvolvedInLegalProceedingAsCA,
            [`${SECT_INFORMATION_INSOLVENCY}-involved-legal-details`]: licenceNode.InsolvencyLegalProceedingsInfo.involvedInLegalProceedingAsCADetails,
            [`${SECT_INFORMATION_INSOLVENCY}-compromise-creditors`]: licenceNode.InsolvencyLegalProceedingsInfo.isEnteredIntoAnyCompromiseWithCreditors,
            [`${SECT_INFORMATION_INSOLVENCY}-compromise-creditors-details`]: licenceNode.InsolvencyLegalProceedingsInfo.enteredIntoAnyCompromiseWithCreditorsDetails,
            [`${SECT_INFORMATION_INSOLVENCY}-receiver-manager-shareholder`]: licenceNode.InsolvencyLegalProceedingsInfo.isReceiverAppointedToApplicantCompany,
            [`${SECT_INFORMATION_INSOLVENCY}-receiver-manager-shareholder-details`]: licenceNode.InsolvencyLegalProceedingsInfo.receiverAppointedToApplicantCompanyDetails,
            [`${SECT_INFORMATION_INSOLVENCY}-fraudulent-dishonest`]: licenceNode.InsolvencyLegalProceedingsInfo.isConvictedOfAnOffence,
            [`${SECT_INFORMATION_INSOLVENCY}-fraudulent-dishonest-details`]: licenceNode.InsolvencyLegalProceedingsInfo.convictedOfAnOffenceDetails
        })
    };

    const licence = {
        id: 'application-details',
        formID: 'application-details',
        hasError: false,
        title: 'Licence Details',
        values: applicationDetailsValues
    };

    return licence;
};

const acaPayload = {
    formatACA
};

export default acaPayload;
